<script setup lang="ts">
import { graphql } from '@/gql';
import SimplePageContainer from 'theme/components/molecules/SimplePageContainer.vue';
import { extractNodeList } from 'theme/utils/gql';
import PartnerPontaBannerList from '~/components/organisms/_lp/PartnerPontaBannerList/PartnerPontaBannerList.vue';
import QuestionnaireListTemplate from '~/components/templates/QuestionnaireListTemplate/QuestionnaireListTemplate.vue';

const query = graphql(`
  query PartnerPontaQuestionnaires($pagination: QuestionnairePaginationInput!) {
    questionnaires(
      channel: PONTA
      order: CLOSE_TIME_DESC
      pagination: $pagination
    ) {
      edges {
        node {
          ...QuestionnaireListTemplate
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const PAGE_SIZE = 6;

const {
  result,
  loading,
  refetch,
  fetchMore: fetchMoreQuestionnaires,
} = useQuery(
  query,
  {
    pagination: {
      first: PAGE_SIZE,
    },
  },
  { prefetch: false, fetchPolicy: 'cache-and-network' }
);

const questionnaireList = computed(() =>
  result.value?.questionnaires
    ? extractNodeList(result.value.questionnaires)
    : []
);

onMounted(() => {
  const { isAuthenticated } = useAuthenticationState();

  if (isAuthenticated.value) {
    refetch({
      pagination: {
        first: PAGE_SIZE,
        after: null,
      },
    });
  }
});

useAppMeta({
  title: `すべてのアンケート - オカネコ for Ponta - 3分でかんたん家計診断`,
  hasTitleSuffix: false,
});

definePageMeta({
  layout: 'partner-ponta',
});

const alert = useAlert();

const fetchMore = (): void => {
  if (!result.value?.questionnaires.pageInfo.hasNextPage) {
    alert.warning('すべてのアンケートの取得が完了しました');
    return;
  }
  const after = result.value?.questionnaires.pageInfo.endCursor ?? null;

  fetchMoreQuestionnaires({
    variables: {
      pagination: {
        after,
        first: PAGE_SIZE,
      },
    },
    updateQuery: (previousResult, { fetchMoreResult }) => {
      const { edges, __typename } = previousResult.questionnaires;
      return {
        ...previousResult,
        questionnaires: {
          __typename,
          edges: [...edges, ...(fetchMoreResult?.questionnaires.edges ?? [])],
          pageInfo: fetchMoreResult?.questionnaires.pageInfo ?? {
            hasNextPage: false,
          },
        },
      };
    },
  });
};
</script>

<template>
  <SimplePageContainer :class="$style.page">
    <QuestionnaireListTemplate
      :questionnaires="questionnaireList"
      :back-to="{
        path: '/partner/ponta',
      }"
      :loading="loading"
      :has-next-page="result?.questionnaires.pageInfo.hasNextPage"
      @fetch-more="fetchMore"
    />
    <PartnerPontaBannerList :class="$style.page__banners" />
  </SimplePageContainer>
</template>

<style lang="scss" module>
.page {
  &__banners {
    &:nth-child(n + 2) {
      margin-top: 56px;

      @include tablet-upper {
        margin-top: 80px;
      }
    }
  }

  &__pending {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.pending {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 240px;
}
</style>
